import React, { type FunctionComponent, useEffect, useState } from 'react'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { type MetricCardProps, colors } from '@retailer-platform/shared-components'
import {
  comparisonPeriodCopy,
  dateRangePickerPresetLabels,
} from '@retailer-platform/shared-components/src/common/date-range-picker/utils/DateRangePickerPresets.utils'
import {
  InsightsPortalOrderDirection,
  type InsightsPortalSchema,
} from 'domains/insights-portal/api'
import { insightsSchemaConfigs } from 'domains/insights-portal/schemas/types/schema.types'
import { calculateComparisonPeriod } from 'domains/insights-portal/utils/insights-portal.utils'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import {
  transformMultiGroupDataToPrimaryTrendData,
  transformMultiGroupDataToComparisonTrendData,
  transformMultiGroupDataToBenchmarkTrendData,
  mergeDataGroupedBySpecificMetricSchema,
  groupMetricsBySpecificSchema,
  extractMetrics,
} from 'domains/insights-portal/widgets/KPIOverviewTrend/KpiOverviewTrend.utils'
import {
  type GroupByOptionValue,
  startDayToGroupByValue,
} from 'domains/insights-portal/components/date-range-picker/types/datePicker.types'
import { insightsColors } from 'domains/insights-portal/utils/insight-colors'
import { type AnalyticTrackingData } from 'domains/insights-portal/utils/insights-portal.types'
import { withInsightsWidgetErrorBoundary } from 'domains/insights-portal/components/error-boundary/InsightsPortalErrorBoundary'
import {
  createSchemaMetricsHooks,
  useFilterParamsContext,
  createMultiGroupDataHook,
} from './KpiOverviewTrend.api'
import { useCurrentAndLastPeriodMetricsForAllSchemas } from './KpiOverviewTrend.hooks'
import { KpiTrendFill } from './components/KpiTrendFill'
import { KpiTrendLine } from './components/KpiTrendLine'
import { type KpiTrendLineProps } from './utils/KpiOverviewTrend.types'

export interface MetricInfo {
  apiMetric: string
  metricName: string
  formatter?: (value: number) => string
  tooltip?: string
  desiredDirection?: MetricCardProps['desiredDirection']
  benchmarkMetric?: string
  historicalMetric?: string
  /**
   * additionalMetricsToSendInRequest is for a rare case for when a indicator_metrics call (useMetrics)
   * requires other metrics to be sent along in the metrics array in the request
   */
  additionalMetricsToSendInRequest?: string[]
  metricSpecificSchema?: InsightsPortalSchema
}

export interface MetricItemWithData {
  value: number
  comparisonValue: number
  apiMetric: string
  metricName: string
  desiredDirection: 'positive' | 'negative'
  tooltip: string
  formatter: (value: number) => string
}

export interface KpiOverviewTrendProps {
  metricInfo: MetricInfo[]
  schema: InsightsPortalSchema
  metricsSchema?: InsightsPortalSchema
  groupBys: SimpleOption[]
  title?: string
  dashboardName?: string
  analyticTrackingData?: AnalyticTrackingData
  showExportMenu?: boolean
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  chartContainerStyles?: React.CSSProperties
  // This will allow additional series' data to present itself in the chart (tooltip) while hiding the trend lines
  showAdditionalSeriesTrendLines?: boolean
  variant?: 'trend-line' | 'trend-fill'
  sideComponent?: React.ReactNode
}

const comparisonColor = colors.HIGHLIGHT.REGULAR
const benchmarkColor = insightsColors.darkGray

export const _KpiOverviewTrend: FunctionComponent<KpiOverviewTrendProps> = ({
  metricInfo = [],
  schema,
  metricsSchema,
  title,
  groupBys,
  analyticTrackingData,
  showExportMenu,
  filtersObjOverride,
  showAdditionalSeriesTrendLines = true,
  variant,
  chartContainerStyles,
  sideComponent,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<string>(metricInfo[0]?.apiMetric)

  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByOptionValue>(groupBys[0].value)

  const {
    selectedFiltersValues: { comparisonType, weekStartDay },
    dates: { dateRange, from, to },
  } = useFilterParamsContext()
  const weekGroupBy: GroupByOptionValue = startDayToGroupByValue[weekStartDay]

  const selectedMetricName = metricInfo.find(
    metricInfo => metricInfo.apiMetric === selectedMetric
  ).metricName
  useEffect(() => {
    const groupByWeekIsSelected = Object.values(startDayToGroupByValue).includes(selectedGroupBy)

    if (groupByWeekIsSelected) {
      setSelectedGroupBy(weekGroupBy)
    }
  }, [selectedGroupBy, setSelectedGroupBy, weekGroupBy])

  const { useCurrentPeriodMetrics, useLastPeriodMetrics } = createSchemaMetricsHooks(
    metricsSchema || schema,
    insightsSchemaConfigs[metricsSchema || schema].transformFilterParamsToApiFilterSelections
  )
  const selectedTrendLineSchema =
    metricInfo.find(metric => metric.apiMetric === selectedMetric).metricSpecificSchema || schema

  const { useMultiGroupData } = createMultiGroupDataHook(
    selectedTrendLineSchema,
    insightsSchemaConfigs[schema].transformFilterParamsToApiFilterSelections
  )

  const listOfMetrics: string[] = metricInfo.map(({ apiMetric }) => apiMetric)

  const {
    listOfMetricsWithoutSpecificSchemas,
    listOfBenchmarkMetrics,
    listOfHistoricalMetrics,
    selectedMetricHistorical,
    selectedMetricFormatter = v => v.toString(),
  } = extractMetrics(metricInfo, selectedMetric)

  //const { selectedFiltersValues, dates } = useFilterParamsContext()

  const { data: currentPeriodData, loading: currentMetricsLoading } = useCurrentPeriodMetrics(
    listOfMetricsWithoutSpecificSchemas,
    false,
    null,
    filtersObjOverride
  )
  const { data: lastPeriodData, loading: lastMetricsLoading } = useLastPeriodMetrics(
    listOfMetricsWithoutSpecificSchemas,
    false,
    null,
    null,
    filtersObjOverride
  )

  // filter out metric objects with specific schemas
  const specificSchemaMetricObjectsGroupedBySchema = groupMetricsBySpecificSchema(metricInfo)

  const {
    currentPeriodDataGroupedBySpecificMetricSchema,
    currentPeriodLoadingGroupedBySpecificMetric,
    lastPeriodDataGroupedBySpecificMetricSchema,
    lastPeriodLoadingGroupedBySpecificMetric,
  } = useCurrentAndLastPeriodMetricsForAllSchemas(specificSchemaMetricObjectsGroupedBySchema)

  // combine the specific metric data with their respective current and last period data
  const currentPeriodSpecificMetricData = mergeDataGroupedBySpecificMetricSchema(
    currentPeriodDataGroupedBySpecificMetricSchema
  )
  const lastPeriodSpecificMetricData = mergeDataGroupedBySpecificMetricSchema(
    lastPeriodDataGroupedBySpecificMetricSchema
  )

  const fullCurrentPeriodDataObject = {
    ...currentPeriodData,
    ...currentPeriodSpecificMetricData,
  }
  const fullLastPeriodDataObject = {
    ...lastPeriodData,
    ...lastPeriodSpecificMetricData,
  }

  const { data: multiGroupData, loading: isLoadingMultiGroupData } = useMultiGroupData({
    metrics: [
      ...listOfMetrics,
      ...listOfBenchmarkMetrics,
      ...listOfHistoricalMetrics,
      ...listOfMetricsWithoutSpecificSchemas,
    ],
    groupBys: [selectedGroupBy],
    orderBys: [{ columnName: selectedGroupBy, orderDirection: InsightsPortalOrderDirection.Asc }],
    includeHistoricalDate: true,
    historicalGroupBy: `${selectedGroupBy}_HISTORICAL`,
    filtersObjOverride: filtersObjOverride,
  })
  const listOfMetricItemsWithData = metricInfo.map(
    ({ apiMetric, metricName, desiredDirection, formatter, tooltip }) => ({
      value: Number(fullCurrentPeriodDataObject[apiMetric]),
      comparisonValue: Number(fullLastPeriodDataObject[apiMetric]),
      apiMetric: apiMetric,
      metricName: metricName,
      desiredDirection: desiredDirection,
      tooltip: tooltip,
      formatter: formatter,
    })
  )

  const messages = useDomainMessages({
    viewBy: 'insightsPortalDomain.controls.viewBy.label',
    benchmarkComparison: 'insightsPortalDomain.benchmarkComparison',
    currentPeriod: 'insightsPortalDomain.overallPerformance.currentPeriodLabel',
  })

  const primarySeriesName = `${
    dateRangePickerPresetLabels[dateRange] || messages.currentPeriod
  } (${from.format('MMM D')} - ${to.format('MMM D')})`
  const { from: comparisonFrom, to: comparisonTo } = calculateComparisonPeriod(
    from,
    to,
    comparisonType
  )
  const comparisonSeriesName = `${comparisonPeriodCopy[comparisonType]} (${comparisonFrom.format(
    'MMM D'
  )} - ${comparisonTo.format('MMM D')})`
  const benchmarkSeriesName = messages.benchmarkComparison
  const primaryData = transformMultiGroupDataToPrimaryTrendData(
    multiGroupData,
    selectedMetric,
    selectedGroupBy
  )
  const comparisonData = transformMultiGroupDataToComparisonTrendData(
    multiGroupData,
    selectedMetricHistorical,
    selectedGroupBy
  )
  const benchmarkData = transformMultiGroupDataToBenchmarkTrendData(
    multiGroupData,
    selectedMetric,
    selectedGroupBy
  )

  const commonProps: KpiTrendLineProps = {
    primaryData: primaryData,
    primaryColor: colors.HIGHLIGHT.DARK,
    comparisonData: comparisonData,
    comparisonColor: comparisonColor,
    benchmarkColor: benchmarkColor,
    comparisonSeriesName: comparisonSeriesName,
    analyticTrackingData: analyticTrackingData,
    groupBys: groupBys,
    showAdditionalSeriesTrendLines: showAdditionalSeriesTrendLines,
    benchmarkSeriesName: benchmarkSeriesName,
    selectedGroupBy: selectedGroupBy,
    primarySeriesName: primarySeriesName,
    showExportMenu: showExportMenu,
    setSelectedGroupBy: setSelectedGroupBy,
    isLoading:
      currentMetricsLoading ||
      lastMetricsLoading ||
      lastPeriodLoadingGroupedBySpecificMetric.some(loading => loading === true) ||
      currentPeriodLoadingGroupedBySpecificMetric.some(loading => loading === true),

    setSelectedMetric: setSelectedMetric,
    listOfMetricItemsWithData: listOfMetricItemsWithData,
    title: title,
    selectedMetric: selectedMetric,
    selectedMetricFormatter: selectedMetricFormatter,
    selectedMetricName: selectedMetricName,
    benchmarkData: benchmarkData,
    isLoadingMultiGroupData: isLoadingMultiGroupData,
    chartContainerStyles: chartContainerStyles,
    sideComponent: sideComponent,
  }

  switch (variant) {
    case 'trend-fill':
      return (
        <div>
          <KpiTrendFill {...commonProps} />
        </div>
      )
    default:
      return (
        <div>
          <KpiTrendLine {...commonProps} />
        </div>
      )
  }
}

export const KpiOverviewTrend = withInsightsWidgetErrorBoundary(_KpiOverviewTrend)
